// src/pages/Plans.jsx (o donde lo tengas)
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Swiper, SwiperSlide } from 'swiper/react';
import { EffectCoverflow, Pagination, Keyboard, Mousewheel } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/effect-coverflow';
import 'swiper/css/pagination';

import api from '../api';
import './Plans.css';

export default function Plans() {
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);

  const [me, setMe] = useState(null);
  const [loadingMe, setLoadingMe] = useState(true);

  const [campusLoading, setCampusLoading] = useState(false);

  /* ==================== Fetchers ==================== */
  const fetchPlans = useCallback(async () => {
    try {
      setPlansLoading(true);
      const { data } = await api.get('plans'); // SIN slash
      setPlans(data || []);
    } catch {
      setPlans([]);
    } finally {
      setPlansLoading(false);
    }
  }, []);

  const fetchMe = useCallback(async () => {
    try {
      setLoadingMe(true);
      const { data } = await api.get('me'); // SIN slash

      // Normaliza forma de /me:
      // - /me => { id,name,..., subscription: {...} }
      // - login/register => { user: {...}, subscription: {...} }
      const subscription =
        data?.subscription ??
        data?.user?.subscription ??
        null;

      const meNorm = {
        // por si quieres usar datos del user luego
        user: data?.user ?? data,
        subscription,
      };

      setMe(meNorm);
    } catch {
      setMe(null);
    } finally {
      setLoadingMe(false);
    }
  }, []);

  useEffect(() => { fetchPlans(); }, [fetchPlans]);
  useEffect(() => { fetchMe(); }, [fetchMe]);

  // Revalida al volver del checkout o cambiar de pestaña/ventana
  useEffect(() => {
    const onFocus = () => fetchMe();
    const onVis = () => { if (document.visibilityState === 'visible') fetchMe(); };
    const onStorage = (e) => {
      if (e.key === 'api_token') fetchMe();
    };

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('storage', onStorage);
    };
  }, [fetchMe]);

  /* ==================== Acciones ==================== */
  const subscribe = async (priceId) => {
    try {
      const res = await api.post('subscribe', { priceId }); // SIN slash
      if (res?.data?.url) {
        window.location.href = res.data.url; // Stripe checkout o similar
      }
    } catch (e) {
      alert('No se pudo iniciar la compra. Intenta de nuevo.');
    }
  };

  const goToLaravel = async () => {
    try {
      setCampusLoading(true);
      const { data } = await api.post('create-magic-link'); // SIN slash
      if (data?.url) window.location.href = data.url;
      else alert('No se pudo generar el enlace al campus.');
    } catch {
      alert('No pude abrir el campus. Intenta de nuevo.');
    } finally {
      setCampusLoading(false);
    }
  };

  /* ==================== Helpers UI ==================== */
  const detectKind = (p) => {
    const meta = p.metadata || {};
    if (meta.school_mode === 'pro' || p.type === 'one_time' || p.interval === 'one_time') return 'one_time';
    if (meta.school_mode === 'mensual' || p.interval === 'month' || p.recurring === 'month') return 'monthly';
    return /pro|unico|único|lifetime/i.test(p.name) ? 'one_time' : 'monthly';
  };

  const normalized = useMemo(() => {
    if (!plans?.length) return [];
    const withKind = plans.map(p => ({ ...p, _kind: detectKind(p) }));
    const anyPopular = withKind.some(p => p.popular);
    if (anyPopular) return withKind;
    const proIndex = withKind.findIndex(p => p._kind === 'one_time');
    return withKind.map((p, i) => ({ ...p, popular: proIndex !== -1 ? i === proIndex : i === 1 }));
  }, [plans]);

  // ¿El usuario tiene acceso (algún plan activo/trial)?
  const hasAccess = useMemo(() => {
    const s = me?.subscription;
    if (!s) return false;
    if (!s.status) return true; // si no envías status, asumimos activo
    return ['active', 'trialing'].includes(String(s.status).toLowerCase());
  }, [me]);

  // Plan actual según price_id devuelto por /me
  const currentPlan = useMemo(() => {
    if (!hasAccess || !normalized.length) return null;
    const priceId = me?.subscription?.price_id || me?.price_id || me?.plan_price_id;
    if (!priceId) return null;

    // Intenta varias propiedades por si tu backend cambió el nombre
    const matchByPriceId = (p) => {
      const candidates = [p.id, p.price_id, p.stripe_price, p.stripe_price_id].filter(Boolean);
      return candidates.includes(priceId);
    };
    return normalized.find(matchByPriceId) || null;
  }, [hasAccess, me, normalized]);

  const currentKind = currentPlan?._kind;

  // Upgrades sugeridos (de mensual -> único)
  const upgradeTargets = useMemo(() => {
    if (!normalized.length || !currentKind) return [];
    if (currentKind === 'monthly') {
      return normalized
        .filter(p => p._kind === 'one_time')
        .sort((a, b) => a.amount - b.amount);
    }
    return [];
  }, [normalized, currentKind]);

  const preferredUpgrade = upgradeTargets[0] || null;

  // Animaciones
  const containerV = {
    hidden: { opacity: 0 },
    show: { opacity: 1, transition: { staggerChildren: 0.12, delayChildren: 0.05 } },
    exit: { opacity: 0 }
  };
  const cardV = {
    hidden: { opacity: 0, y: 24, scale: 0.98 },
    show: { opacity: 1, y: 0, scale: 1, transition: { type: 'spring', stiffness: 220, damping: 22 } },
    exit: { opacity: 0, y: 20, scale: 0.98, transition: { duration: 0.18 } }
  };

  const slides = normalized.slice(0, 5);
  const formatPrice = (cents) => (cents / 100).toFixed(2);

  return (
    <div className="plans-wrapper">
      <motion.div
        className="plans-container"
        variants={containerV}
        initial="hidden"
        animate="show"
        exit="exit"
      >
        <motion.h1 className="plans-title" initial={{ y: 14, opacity: 0 }} animate={{ y: 0, opacity: 1 }}>
          {hasAccess ? 'Tu plan y opciones' : 'Elige un plan'}
        </motion.h1>

        {/* ===== Banner: Plan actual + Upgrade + Ir al campus ===== */}
        <AnimatePresence>
          {(loadingMe || hasAccess) && (
            <motion.section
              className="current-plan-banner"
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              style={{
                display: 'grid',
                gap: '12px',
                padding: '16px',
                borderRadius: '12px',
                background: 'rgba(20,32,106,0.06)'
              }}
            >
              {loadingMe ? (
                <div className="skeleton" style={{ height: 20, width: 240, background: '#eee', borderRadius: 6 }} />
              ) : hasAccess && currentPlan ? (
                <>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
                    <strong>Plan actual:</strong>
                    <span>{currentPlan.name}</span>
                    <span style={{
                      padding: '2px 8px',
                      borderRadius: 999,
                      background: currentKind === 'one_time' ? '#DCFCE7' : '#E0E7FF',
                      color: currentKind === 'one_time' ? '#166534' : '#1E3A8A',
                      fontSize: 12,
                      fontWeight: 700
                    }}>
                      {currentKind === 'one_time' ? 'PAGO ÚNICO' : 'MENSUAL'}
                    </span>
                    <span style={{ opacity: .7 }}>
                      MX$ {formatPrice(currentPlan.amount)}{currentKind === 'one_time' ? '/único' : '/mes'}
                    </span>
                  </div>

                  <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                    {preferredUpgrade ? (
                      <>
                        <span>¿Quieres desbloquear todo?</span>
                        <button
                          className="plan-btn primary"
                          onClick={() => subscribe(preferredUpgrade.id)}
                          style={{ padding: '8px 12px' }}
                        >
                          Mejorar a {preferredUpgrade.name} (MX$ {formatPrice(preferredUpgrade.amount)} / único)
                        </button>
                      </>
                    ) : (
                      <div style={{ opacity: .8 }}>Ya cuentas con el plan más alto. 🎉</div>
                    )}

                    <motion.button
                      className="plan-btn ghost"
                      whileTap={{ scale: 0.97 }}
                      onClick={goToLaravel}
                      disabled={campusLoading}
                      style={{ padding: '8px 12px', marginLeft: 'auto' }}
                      title="Abrir Campus (Laravel)"
                    >
                      {campusLoading ? 'Abriendo campus…' : 'Ir al campus (Laravel)'}
                    </motion.button>
                  </div>
                </>
              ) : null}
            </motion.section>
          )}
        </AnimatePresence>

        {/* ===== Swiper Coverflow (intro) — solo si NO tiene plan ===== */}
        {slides.length > 0 && !hasAccess && (
          <section className="plans-swiper">
            <div className="intro">
              <span>Descubre</span>
              <h2>Modalidades de la escuela</h2>
              <hr />
              <p>Elige pago único para desbloquear todos los módulos, o mensual para avanzar paso a paso.</p>
            </div>

            <Swiper
              className="coverflow"
              modules={[EffectCoverflow, Pagination, Keyboard, Mousewheel]}
              effect="coverflow"
              grabCursor
              centeredSlides
              coverflowEffect={{ rotate: 0, stretch: 0, depth: 100, modifier: 3, slideShadows: true }}
              keyboard={{ enabled: true }}
              mousewheel={{ thresholdDelta: 70 }}
              loop
              pagination={{ clickable: true }}
              breakpoints={{
                640: { slidesPerView: 2 },
                768: { slidesPerView: 1 },
                1024: { slidesPerView: 2 },
                1560: { slidesPerView: 3 },
              }}
            >
              {slides.map((p, idx) => {
                const price = formatPrice(p.amount);
                const kind = p._kind;
                let cta = kind === 'one_time' ? 'Pagar completo' : 'Comenzar por mes';
                return (
                  <SwiperSlide key={p.id} className={`slide slide--${(idx % 5) + 1}`}>
                    <div className="slide-content">
                      <h3>{p.name}</h3>
                      <p className="slide-price">
                        <span>MX$</span> {price} <small>{kind === 'one_time' ? '/único' : '/mes'}</small>
                      </p>
                      <p className="slide-text">
                        {kind === 'one_time'
                          ? 'Pago único. Desbloqueo total de módulos y acceso de por vida.'
                          : 'Pago mensual. Desbloquea módulos progresivamente y cancela cuando quieras.'}
                      </p>
                      <button className="slide-btn" onClick={() => subscribe(p.id)}>{cta}</button>
                    </div>
                  </SwiperSlide>
                );
              })}
            </Swiper>
          </section>
        )}

        {/* ===== Grid de tarjetas — solo si NO tiene plan ===== */}
        {!hasAccess && (
          <motion.div className="plans-grid" variants={containerV}>
            <AnimatePresence mode="popLayout">
              {normalized.map(plan => {
                const kind = plan._kind;
                const price = formatPrice(plan.amount);
                const isPopular = !!plan.popular;
                const badgeText = kind === 'one_time' ? 'PAGO ÚNICO' : 'MENSUAL';
                const ribbonText = kind === 'one_time' ? 'MEJOR VALOR' : 'FLEXIBLE';
                const ctaText = kind === 'one_time' ? 'Pagar completo y desbloquear todo' : 'Comenzar por mes';

                return (
                  <motion.article
                    key={plan.id}
                    className={`plan-card ${isPopular ? 'popular' : ''}`}
                    variants={cardV}
                    layout
                    whileHover={{
                      rotateX: 2,
                      rotateY: -2,
                      translateY: -6,
                      boxShadow: '0 22px 52px rgba(20,32,106,0.22)'
                    }}
                  >
                    {isPopular && <div className="plan-ribbon">{ribbonText}</div>}

                    <header className="plan-header">
                      <h2 className="plan-name">{plan.name}</h2>
                      <span className="plan-type">{badgeText}</span>
                    </header>

                    <div className="plan-price">
                      <span className="currency">MX$</span>
                      <span className="amount">{price}</span>
                      <span className="per">{kind === 'one_time' ? '/único' : '/mes'}</span>
                    </div>

                    <div className="plan-promo">
                      {kind === 'monthly' ? 'Accede hoy con pago mensual' : 'Desbloqueo total de módulos'}
                    </div>

                    <motion.button
                      className={`plan-btn ${isPopular ? 'primary' : 'ghost'}`}
                      whileTap={{ scale: 0.97 }}
                      onClick={() => subscribe(plan.id)}
                      title="Continuar"
                    >
                      {ctaText}
                    </motion.button>

                    <div className="plan-divider" />

                    <ul className="plan-features">
                      {(plan.features?.length ? plan.features : (
                        kind === 'one_time'
                          ? ['Desbloquea los 12 módulos al instante', 'Acceso de por vida al contenido', 'Certificado final incluido', 'Soporte prioritario']
                          : ['Acceso mes a mes', 'Desbloquea módulos progresivamente', 'Pausa o cancela cuando quieras', 'Soporte estándar']
                      )).map((f, idx) => (
                        <li className="feature-item" key={idx}>
                          <span className="dot" />
                          {f}
                        </li>
                      ))}
                    </ul>

                    <span className="card-shine" aria-hidden />
                  </motion.article>
                );
              })}
            </AnimatePresence>
          </motion.div>
        )}

        {/* Si TIENES plan pero currentPlan no se resolvió (p.ej. price_id no está en catálogo),
            muestra al menos el botón al campus */}
        {hasAccess && !currentPlan && !loadingMe && (
          <div style={{ marginTop: 24 }}>
            <motion.button
              className="plan-btn primary"
              whileTap={{ scale: 0.97 }}
              onClick={goToLaravel}
              disabled={campusLoading}
            >
              {campusLoading ? 'Abriendo campus…' : 'Ir al campus (Laravel)'}
            </motion.button>
          </div>
        )}

        {/* Estado vacío / carga de planes */}
        {plansLoading && (
          <div style={{ marginTop: 24, opacity: .7 }}>
            Cargando planes…
          </div>
        )}
        {!plansLoading && !plans?.length && !hasAccess && (
          <div style={{ marginTop: 24, opacity: .7 }}>
            No hay planes disponibles por ahora.
          </div>
        )}
      </motion.div>
    </div>
  );
}
